// Start einer Programm-Website: Schriften/Stile, Abfragen, Sitzung, Rückmeldungen und der Router mit genau den Seiten
// dieses Programms. Jede Seite ist geschützt (ohne Sitzung → Anmelden, ohne Recht → 403). Adressen anderer Programme
// gehen an den Rahmen (im Rahmen) bzw. an den Torweg (/app/…); läuft das Programm allein, gibt es einen Hinweis.
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, StrictMode, Suspense, type ComponentType, type LazyExoticComponent } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, Navigate, type RouteObject } from "react-router";
import { RouterProvider } from "react-router/dom";
import { ApiFehler } from "../api/client";
import { RueckmeldungProvider } from "../bausteine/rueckmeldung";
import { AppShell } from "../huelle/AppShell";
import { ProgrammKontext } from "../kern/programm";
import { programmBasis } from "../kern/rahmen";
import { SitzungProvider } from "../kern/sitzung";
import { gemerktesThema, themaAnwenden } from "../kern/thema";
import { Absturz } from "../seiten/Fehlerseiten";
import { BEREICHE, programmFuerPfad, SEITEN, UMZUEGE, type ProgrammId } from "./katalog";
import { DetailMitRecht, Fremd, Geschuetzt, SeiteMitRecht } from "./Wachen";

export type Inhalte = Readonly<Record<string, LazyExoticComponent<ComponentType>>>;
/** Detailseiten mit Parametern (nicht in der Seitenleiste), z. B. bestand/objekt/:nr. */
export type Detail = { pfad: string; rechte: readonly string[]; inhalt: LazyExoticComponent<ComponentType> };
/** Seiten ohne Hülle (Vollbild fürs Handy), z. B. die ZeitApp unter /zeit. */
export type Vollbild = { pfad: string; inhalt: LazyExoticComponent<ComponentType> };

const Anmelden = lazy(() => import("../seiten/Anmelden"));

function routen(id: ProgrammId, inhalte: Inhalte, details: readonly Detail[], vollbild: readonly Vollbild[]): RouteObject[] {
  const bereiche = new Set(BEREICHE.filter((b) => b.programm === id).map((b) => b.id));
  const eigene = SEITEN.filter((s) => bereiche.has(s.bereich));
  const kinder: RouteObject[] = [
    ...eigene.map((seite): RouteObject => (seite.pfad === "/"
      ? { index: true, element: <SeiteMitRecht seite={seite} Inhalt={inhalte[seite.pfad]} /> }
      : { path: seite.pfad.slice(1), element: <SeiteMitRecht seite={seite} Inhalt={inhalte[seite.pfad]} /> })),
    ...details.map((d): RouteObject => ({ path: d.pfad, element: <DetailMitRecht rechte={d.rechte} Inhalt={d.inhalt} /> })),
    ...Object.entries(UMZUEGE).filter(([, neu]) => programmFuerPfad(neu) === id)
      .map(([alt, neu]): RouteObject => ({ path: alt.slice(1), element: <Navigate to={neu} replace /> })),
    ...(eigene.some((s) => s.pfad === "/") ? [] : [{ index: true, element: <Fremd eigenes={id} /> }]),
    { path: "*", element: <Fremd eigenes={id} /> },
  ];
  return [
    { path: "/anmelden", element: <Suspense fallback={null}><Anmelden /></Suspense>, errorElement: <Absturz /> },
    ...vollbild.map((v): RouteObject => ({ path: v.pfad, errorElement: <Absturz />,
      element: <Geschuetzt><Suspense fallback={null}><v.inhalt /></Suspense></Geschuetzt> })),
    { path: "/", element: <Geschuetzt><AppShell /></Geschuetzt>, errorElement: <Absturz />, children: kinder },
  ];
}

export function abfragenErstellen(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // 401/403/404 nicht wiederholen – das ändert sich durch Warten nicht.
        retry: (anzahl, fehler) => !(fehler instanceof ApiFehler && fehler.code < 500) && anzahl < 2,
      },
    },
  });
}

/** Thema des Rahmens/anderer Fenster übernehmen (gleicher Speicher). */
/** Schriftgröße aus „Meine Einstellungen“ (im Browser gemerkt) – gilt in jedem Programm. */
function schriftAnwenden(): void {
  let wert: string | null = null;
  try {
    wert = localStorage.getItem("imho3.schrift");
  } catch {
    // ohne Speicher: normal
  }
  if (wert === "gross" || wert === "sehr_gross") document.documentElement.dataset.schrift = wert;
  else delete document.documentElement.dataset.schrift;
}

export function themaVerfolgen(): void {
  themaAnwenden(gemerktesThema());
  schriftAnwenden();
  window.addEventListener("storage", (e) => {
    if (e.key === "imho3.thema") themaAnwenden(gemerktesThema());
    if (e.key === "imho3.schrift") schriftAnwenden();
  });
}

export function starteProgramm({ id, inhalte, details = [], vollbild = [] }: {
  id: ProgrammId; inhalte: Inhalte; details?: readonly Detail[]; vollbild?: readonly Vollbild[];
}): void {
  themaVerfolgen();
  const basis = programmBasis();
  const router = createBrowserRouter(routen(id, inhalte, details, vollbild), basis ? { basename: basis } : undefined);
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <QueryClientProvider client={abfragenErstellen()}>
        <SitzungProvider>
          <RueckmeldungProvider>
            <ProgrammKontext.Provider value={id}>
              <RouterProvider router={router} />
            </ProgrammKontext.Provider>
          </RueckmeldungProvider>
        </SitzungProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}
